import React, { useEffect, useState, useMemo } from 'react';
import {
  Container,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  CircularProgress,
  Alert,
  TextField,
  TableContainer,
  Paper,
  Grid,
  Stack,
  Chip,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import { useAuth } from '../../AuthContext';
import Header1 from '../Header/Header1';
import Footer2 from '../Footer/Footer2';

const API_URL = process.env.REACT_APP_API_URL;

/* ---------- STYLED MODERN COMPONENTS ---------- */

const GlassCard = styled(Paper)(() => ({
  backdropFilter: 'blur(10px)',
  background: 'rgba(255, 255, 255, 0.06)',
  borderRadius: '15px',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
}));

const HoverRow = styled(TableRow)(({ theme }) => ({
  transition: 'background 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const StickyHeaderCell = styled(TableCell)(() => ({
  position: 'sticky',
  top: 0,
  background: 'rgba(25, 25, 25, 0.95)',
  backdropFilter: 'blur(6px)',
  zIndex: 3,
  color: '#fff',
  fontWeight: 'bold',
}));

/* ------------------------------------------------ */

const AdminAmbassadorRequests = () => {
  const { userId } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionMsg, setActionMsg] = useState('');
  const [userMap, setUserMap] = useState({});
  const [searchQuery, setSearchQuery] = useState('');

  // Pagination states
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchUser = async (id) => {
    try {
      const res = await axios.post(`${API_URL}/api/user/`, { userId: id });
      return res.data;
    } catch (err) {
      console.error('Error fetching user info for:', id, err);
      return null;
    }
  };

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/ambassadors/`);
      const data = res.data;
      setRequests(data);

      const uniqueIds = new Set();
      data.forEach((r) => {
        if (r.user_id) uniqueIds.add(r.user_id);
        if (r.reviewed_by) uniqueIds.add(r.reviewed_by);
      });

      const userInfoMap = {};
      await Promise.all(
        [...uniqueIds].map(async (id) => {
          const userData = await fetchUser(id);
          if (userData) userInfoMap[id] = userData;
        })
      );

      setUserMap(userInfoMap);
    } catch (err) {
      console.error('Error fetching ambassador requests:', err);
    } finally {
      setLoading(false);
    }
  };

  const approveAmbassador = async (targetUserId) => {
    try {
      const payload = {
        user_id: targetUserId,
        reviewed_by: userId,
      };
      const res = await axios.patch(`${API_URL}/api/ambassadors/approve/`, payload);
      setActionMsg(res.data.message);
      fetchRequests();
    } catch (err) {
      console.error(err);
      setActionMsg('Error approving ambassador.');
    }
  };

  const revokeAmbassador = async (targetUserId) => {
    try {
      const res = await axios.patch(`${API_URL}/api/ambassadors/revoke/`, {
        user_id: targetUserId,
      });
      setActionMsg(res.data.message);
      fetchRequests();
    } catch (err) {
      console.error(err);
      setActionMsg('Error revoking ambassador.');
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const getFullName = (id) => {
    const user = userMap[id];
    return user ? `${user.name || ''} ${user.surname || ''}`.trim() : 'Loading...';
  };

  /* ---------- SEARCH FILTER ---------- */
  const filteredRequests = requests.filter((r) => {
    const name = getFullName(r.user_id).toLowerCase();
    return (
      name.includes(searchQuery.toLowerCase()) ||
      r.college_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.status.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  /* ---------- COUNTS ---------- */
  const counts = useMemo(() => {
    const total = filteredRequests.length;
    const approved = filteredRequests.filter((r) => r.status === 'approved').length;
    const pending = filteredRequests.filter((r) => r.status === 'pending').length;
    const revoked = filteredRequests.filter((r) => r.status === 'revoked').length;
    return { total, approved, pending, revoked };
  }, [filteredRequests]);

  /* ---------- PAGINATION ---------- */
  const paginatedRequests = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filteredRequests.slice(start, start + rowsPerPage);
  }, [page, filteredRequests, rowsPerPage]);

  const totalPages = Math.ceil(filteredRequests.length / rowsPerPage);

  return (
    <div>
      <Header1 />

      <Container sx={{ mt: 10, mb: 5, minHeight: '80vh' }}>
        <Typography
          variant="h3"
          align="center"
          fontWeight="bold"
          gutterBottom
          sx={{
            backgroundImage: 'linear-gradient(45deg, #00bfff, #ff00ff)',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            letterSpacing: 3,
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            animation: 'fadeIn 1.2s ease',
          }}
        >
          Ambassador Requests
        </Typography>

        {/* Summary Chips */}
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          sx={{ mb: 3, justifyContent: 'center' }}
        >
          <Chip label={`Total: ${counts.total}`} color="primary" variant="filled" />
          <Chip label={`Approved: ${counts.approved}`} color="success" />
          <Chip label={`Pending: ${counts.pending}`} color="warning" />
          <Chip label={`Revoked: ${counts.revoked}`} color="error" />
        </Stack>

        {/* Search + Rows per page */}
        <Grid container spacing={2} sx={{ my: 3 }}>
          <Grid item xs={12} sm={8}>
            <TextField
              label="Search by name, college, or status"
              variant="outlined"
              fullWidth
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Rows per page</InputLabel>
              <Select
                value={rowsPerPage}
                label="Rows per page"
                onChange={(e) => {
                  setRowsPerPage(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {loading ? (
          <Grid container justifyContent="center">
            <CircularProgress />
          </Grid>
        ) : (
          <>
            {actionMsg && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {actionMsg}
              </Alert>
            )}

            <GlassCard>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <StickyHeaderCell>Applicant</StickyHeaderCell>
                      <StickyHeaderCell>College ID</StickyHeaderCell>
                      <StickyHeaderCell>College Name</StickyHeaderCell>
                      <StickyHeaderCell>Address</StickyHeaderCell>
                      <StickyHeaderCell>Status</StickyHeaderCell>
                      <StickyHeaderCell>Reviewed By</StickyHeaderCell>
                      <StickyHeaderCell align="center">Action</StickyHeaderCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {paginatedRequests.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          No results found.
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedRequests.map((r) => (
                        <HoverRow key={r.id}>
                          <TableCell>{getFullName(r.user_id)}</TableCell>
                          <TableCell>{r.college_id}</TableCell>
                          <TableCell>{r.college_name}</TableCell>
                          <TableCell>{r.college_address}</TableCell>
                          <TableCell>
                            <Chip
                              label={r.status}
                              color={
                                r.status === 'approved'
                                  ? 'success'
                                  : r.status === 'pending'
                                  ? 'warning'
                                  : 'error'
                              }
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {r.reviewed_by ? getFullName(r.reviewed_by) : 'Not Reviewed'}
                          </TableCell>
                          <TableCell align="center">
                            {r.status === 'pending' ? (
                              <Button
                                variant="contained"
                                color="success"
                                size="small"
                                onClick={() => approveAmbassador(r.user_id)}
                              >
                                Approve
                              </Button>
                            ) : (
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={() => revokeAmbassador(r.user_id)}
                              >
                                Remove Access
                              </Button>
                            )}
                          </TableCell>
                        </HoverRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </GlassCard>

            {/* Pagination Controls */}
            <Box mt={3} display="flex" justifyContent="center">
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
                size="large"
              />
            </Box>
          </>
        )}
      </Container>

      <Footer2 />
    </div>
  );
};

export default AdminAmbassadorRequests;
